import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';

const Home = () => {
  const [scrollY, setScrollY] = useState(0);
  const heroRef = useRef(null);
  const methodologyRef = useRef(null);
  const aboutRef = useRef(null);
  const esgRef = useRef(null);
  const portfolioRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const observerOptions = {
      threshold: 0.2,
      rootMargin: '0px 0px -100px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-fade-in');
        }
      });
    }, observerOptions);

    const elements = document.querySelectorAll('.fade-on-scroll');
    elements.forEach(el => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const scrollToNext = () => {
    window.scrollTo({
      top: window.innerHeight,
      behavior: 'smooth'
    });
  };

  return (
    <div className="w-full">
      {/* Hero Section */}
      <section 
        ref={heroRef}
        className="relative h-screen flex items-center justify-center overflow-hidden"
        style={{
          background: `linear-gradient(rgba(42, 42, 42, 0.5), rgba(42, 42, 42, 0.6)), url('https://images.unsplash.com/photo-1676718912572-b3ebcff192e3?w=1920&q=80') center/cover`,
          transform: `translateY(${scrollY * 0.5}px)`
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-[#F5F1ED]/20"></div>
        
        {/* Golden ratio circles decoration */}
        <div className="absolute left-10 top-1/4 w-64 h-64 border border-[#A08872]/20 rounded-full"></div>
        <div className="absolute right-20 bottom-1/4 w-96 h-96 border border-[#A08872]/15 rounded-full"></div>
        
        <div className="relative z-10 text-center text-white px-6">
          <div className="mb-8 flex justify-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_finance-portal-237/artifacts/51c1ld3v_Logo%20White.png"
              alt="UK Stage"
              className="w-auto animate-fade-in"
              style={{ 
                height: 'clamp(120px, 15vw, 220px)',
                filter: 'drop-shadow(0 8px 16px rgba(0,0,0,0.4))'
              }}
            />
          </div>
          <p className="text-base sm:text-xl md:text-2xl font-light tracking-wide max-w-3xl mx-auto" 
             style={{ textShadow: '2px 3px 6px rgba(0,0,0,0.6)' }}>
            Building companies that stand the test of time.
          </p>
        </div>

        {/* Scroll indicator */}
        <button 
          onClick={scrollToNext}
          className="absolute bottom-12 left-1/2 transform -translate-x-1/2 text-white border border-white/50 rounded-full p-3 hover:bg-white/10 transition-all duration-300 animate-bounce-slow"
        >
          <ChevronDown className="w-6 h-6" />
        </button>
      </section>

      {/* How We Work Section */}
      <section 
        ref={methodologyRef}
        className="min-h-screen flex items-center justify-center bg-[#F5F1ED] relative overflow-hidden py-20"
      >
        {/* Decorative circles */}
        <div className="absolute left-20 top-1/3 w-[500px] h-[500px] border border-[#A08872]/10 rounded-full"></div>
        <div className="absolute left-32 top-1/3 translate-y-12 w-[300px] h-[300px] border border-[#A08872]/10 rounded-full"></div>
        <div className="absolute left-40 top-1/3 translate-y-20 w-[150px] h-[150px] border border-[#A08872]/10 rounded-full"></div>
        
        <div className="container mx-auto px-6 grid md:grid-cols-2 gap-16 items-center relative z-10">
          <div className="hidden md:block">
            {/* Empty space for circles decoration */}
          </div>
          <div className="fade-on-scroll opacity-0">
            <p className="text-sm text-[#8B7355] uppercase tracking-widest mb-4">HOW WE WORK</p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-serif text-[#2A2A2A] mb-8 leading-tight">
              At UK Stage, we always start with execution.
            </h2>
            <p className="text-lg text-[#3D3D3D] mb-8 leading-relaxed">
              It is the foundation upon which all great businesses are built. Through disciplined capital allocation and structured land aggregation, we work with our partners to build platforms that are truly best in class.
            </p>
            <Link to="/how-we-work">
              <Button 
                variant="outline" 
                className="border-[#2A2A2A] text-[#2A2A2A] hover:bg-[#2A2A2A] hover:text-white transition-all duration-300"
              >
                DISCOVER OUR METHODOLOGY
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* About Us Section */}
      <section 
        ref={aboutRef}
        className="bg-white py-12 md:py-0"
      >
        <div className="container mx-auto px-6 grid md:grid-cols-2 gap-0 md:min-h-screen">
          <div className="fade-on-scroll opacity-0 pr-0 md:pr-12 pb-8 md:pb-0 py-12 md:py-20 flex flex-col justify-center">
            <p className="text-sm text-[#8B7355] uppercase tracking-widest mb-4">ABOUT US</p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-serif text-[#2A2A2A] mb-8 leading-tight">
              About us
            </h2>
            <p className="text-lg text-[#3D3D3D] mb-6 leading-relaxed">
              UK Stage Private Limited is a real estate-anchored investment and development platform built on disciplined capital allocation, structured land aggregation, and long-horizon value creation.
            </p>
            <p className="text-lg text-[#3D3D3D] mb-8 leading-relaxed">
              Established in 2006 in Dehradun, UK Stage began as a focused land acquisition firm and has evolved into a multi-asset real estate platform controlling over 200+ acres across multiple growth corridors.
            </p>
            
            <div className="space-y-4 border-t border-[#EDE8E3] pt-8">
              <Link to="/the-team" className="block py-3 border-b border-[#EDE8E3] text-[#2A2A2A] hover:text-[#8B7355] transition-colors duration-300 uppercase text-sm tracking-wider">
                THE TEAM
              </Link>
              <Link to="/advisory-board" className="block py-3 border-b border-[#EDE8E3] text-[#2A2A2A] hover:text-[#8B7355] transition-colors duration-300 uppercase text-sm tracking-wider">
                ADVISORY BOARD
              </Link>
              <Link to="/join-us" className="block py-3 border-b border-[#EDE8E3] text-[#2A2A2A] hover:text-[#8B7355] transition-colors duration-300 uppercase text-sm tracking-wider">
                JOIN US
              </Link>
            </div>
          </div>
          
          <div className="fade-on-scroll opacity-0 h-[350px] md:h-auto">
            <img 
              src="https://images.unsplash.com/photo-1773470920361-4f6cbb702ce0?w=800&q=80" 
              alt="Luxury Architecture India"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </section>

      {/* ESG Section */}
      <section 
        ref={esgRef}
        className="min-h-screen flex items-center bg-[#EDE8E3] relative overflow-hidden py-20"
      >
        {/* Large decorative circle */}
        <div className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-[#F5F1ED] rounded-full"></div>
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-3xl mx-auto md:ml-auto md:mr-0 fade-on-scroll opacity-0">
            <p className="text-sm text-[#8B7355] uppercase tracking-widest mb-4">DOING GOOD IN BUSINESS</p>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-serif text-[#2A2A2A] mb-8 leading-tight">
              Today, success is more than profit, it is the positive impact we can have on our communities and the frameworks through which decisions are made and capital is allocated. At UK Stage, ESG is built into our DNA.
            </h2>
            <p className="text-lg text-[#3D3D3D] mb-8 leading-relaxed">
              We prioritize tangible asset backing, capital protection through structured entry, execution-led value enhancement, and long-term compounding over short-term speculation.
            </p>
            <Link to="/social-responsibility">
              <Button 
                variant="outline" 
                className="border-[#2A2A2A] text-[#2A2A2A] hover:bg-[#2A2A2A] hover:text-white transition-all duration-300"
              >
                OUR ESG PROMISE
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Portfolio Preview Section */}
      <section 
        ref={portfolioRef}
        className="py-20 bg-white"
      >
        <div className="container mx-auto px-6">
          <div className="mb-12 fade-on-scroll opacity-0">
            <p className="text-sm text-[#8B7355] uppercase tracking-widest mb-4">PORTFOLIO</p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-serif text-[#2A2A2A] mb-4">Our expertise</h2>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <div className="fade-on-scroll opacity-0 group cursor-pointer overflow-hidden">
              <div className="relative h-56 sm:h-72 md:h-96 overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1773470920361-4f6cbb702ce0?w=800&q=80" 
                  alt="Real Estate Development India"
                  className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#2A2A2A]/70 to-transparent"></div>
                <div className="absolute bottom-0 left-0 p-6 text-white">
                  <h3 className="text-2xl font-serif mb-2">Luxury Residential Development</h3>
                  <p className="text-sm opacity-90">200,000+ sq. ft. flagship project</p>
                </div>
              </div>
            </div>
            
            <div className="fade-on-scroll opacity-0 group cursor-pointer overflow-hidden">
              <div className="relative h-56 sm:h-72 md:h-96 overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1769589202125-7895381e49b1?w=800&q=80" 
                  alt="Real Estate Portfolio India"
                  className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#2A2A2A]/70 to-transparent"></div>
                <div className="absolute bottom-0 left-0 p-6 text-white">
                  <h3 className="text-2xl font-serif mb-2">Strategic Land Banking</h3>
                  <p className="text-sm opacity-90">200+ acres across growth corridors</p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="text-center fade-on-scroll opacity-0">
            <Link to="/portfolio">
              <Button 
                variant="outline" 
                className="border-[#2A2A2A] text-[#2A2A2A] hover:bg-[#2A2A2A] hover:text-white transition-all duration-300"
              >
                SEE MORE
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-32 bg-[#2A2A2A] text-white text-center">
        <div className="container mx-auto px-6 fade-on-scroll opacity-0">
          <h2 className="text-3xl sm:text-4xl md:text-6xl font-serif mb-8">Best-in-class businesses start here.</h2>
          <Link to="/contact">
            <Button 
              variant="outline" 
              className="border-white text-white hover:bg-white hover:text-[#2A2A2A] transition-all duration-300 text-sm tracking-wider"
            >
              CONTACT US
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;